import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArrowLeft, RotateCcw, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function ArchivioOperatori() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { selectedStructure, structures = [], user } = useAuth();
  const structMap = Object.fromEntries(structures.map((s) => [s.id, s]));
  const canConv = ["super_admin", "admin", "responsabile", "segreteria"].includes(user?.role);

  const { data = [], isLoading } = useQuery({
    queryKey: ["operatori-archivio", selectedStructure],
    queryFn: async () => (await api.get("/operatori/archivio", { params: { structure_id: selectedStructure } })).data,
  });

  const structNames = (o) => {
    const ids = o.structure_ids && o.structure_ids.length ? o.structure_ids : (o.structure_id ? [o.structure_id] : []);
    return ids.map((id) => structMap[id]?.nome).filter(Boolean).join(", ") || "—";
  };

  const restore = async (o) => {
    if (!window.confirm(`Ripristinare ${o.nome} ${o.cognome}? Tornerà tra gli operatori attivi.`)) return;
    try {
      await api.post(`/operatori/${o.id}/restore`);
      toast.success("Operatore ripristinato");
      qc.invalidateQueries({ queryKey: ["operatori-archivio"] });
      qc.invalidateQueries({ queryKey: ["mod", "operatori"] });
    } catch { toast.error("Errore nel ripristino"); }
  };

  return (
    <div>
      <Button variant="ghost" onClick={() => nav("/operatori")} className="mb-3" data-testid="archivio-op-back"><ArrowLeft size={16} className="mr-1" /> Torna agli operatori</Button>
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Archive size={22} className="text-primary" /> Archivio Operatori</h1>
        <p className="text-sm text-muted-foreground">Operatori cessati o eliminati. Puoi ripristinarli in qualsiasi momento.</p>
      </div>

      <div className="gr-card overflow-x-auto" data-testid="archivio-operatori">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/60">
              <th className="text-left p-3 font-bold min-w-[200px]">Operatore</th>
              <th className="text-left p-3 font-bold">Strutture</th>
              <th className="text-left p-3 font-bold">Qualifica</th>
              <th className="text-left p-3 font-bold">Assunzione</th>
              <th className="text-left p-3 font-bold">Cessazione</th>
              <th className="text-left p-3 font-bold">Motivo</th>
              <th className="p-3 font-bold text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Caricamento...</td></tr>}
            {!isLoading && data.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Nessun operatore in archivio</td></tr>}
            {data.map((o) => (
              <tr key={o.id} className="border-t" data-testid={`archivio-op-row-${o.id}`}>
                <td className="p-3 font-semibold">{o.cognome} {o.nome}</td>
                <td className="p-3">{structNames(o)}</td>
                <td className="p-3">{o.qualifica || "—"}</td>
                <td className="p-3">{o.contratto_inizio || o.data_assunzione ? fmtDate(o.contratto_inizio || o.data_assunzione) : "—"}</td>
                <td className="p-3">{o.data_cessazione ? fmtDate(o.data_cessazione) : "—"}</td>
                <td className="p-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${o.motivo === "eliminato" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} data-testid={`archivio-op-motivo-${o.id}`}>
                    {o.motivo === "eliminato" ? "Eliminato" : "Cessato"}
                  </span>
                </td>
                <td className="p-3 text-center whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => nav(`/operatori/${o.id}`)} data-testid={`archivio-op-scheda-${o.id}`}><FolderOpen size={15} className="text-primary" /></Button>
                  {canConv && <Button size="sm" variant="outline" onClick={() => restore(o)} data-testid={`archivio-op-restore-${o.id}`} className="text-emerald-700 border-emerald-300"><RotateCcw size={14} className="mr-1" /> Ripristina</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
